import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, ShieldCheck as ShieldIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { usePublicConfig } from "@/lib/usePublicConfig";
import { FlagsBENL } from "@/components/Flags";
import ProgressBar from "@/components/analyse/ProgressBar";
import StepType from "@/components/analyse/StepType";
import StepUpload from "@/components/analyse/StepUpload";
import StepPakket from "@/components/analyse/StepPakket";
import StepGegevens from "@/components/analyse/StepGegevens";
import StepBetaling from "@/components/analyse/StepBetaling";
import { fileToB64 } from "@/components/analyse/helpers";

export default function Analyse() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(params.get("type") || "");
  const [regio, setRegio] = useState(params.get("regio") || "");
  const [files, setFiles] = useState([]); // multi-file: [File, File, ...]
  const [text, setText] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [withdrawalWaiver, setWithdrawalWaiver] = useState(false);
  const [wantBizRecs, setWantBizRecs] = useState(false);
  const [bundle, setBundle] = useState(params.get("bundle") === "1");
  const [submitting, setSubmitting] = useState(false);
  // Discount code (per-customer referral code)
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null); // {valid, percent_off, message}
  const [validatingCode, setValidatingCode] = useState(false);

  const publicCfg = usePublicConfig();
  const singlePriceCents = publicCfg?.analysis_price_cents ?? 499;
  const bundlePriceCents = publicCfg?.bundle_price_cents ?? 999;
  const baseAmountCents = bundle ? bundlePriceCents : singlePriceCents;
  const appliedDiscount = discountInfo?.valid
    ? Math.floor((baseAmountCents * discountInfo.percent_off) / 100)
    : 0;
  const finalAmountCents = Math.max(50, baseAmountCents - appliedDiscount);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const canStep2 = !!type;
  const canStep3 = useMemo(() => {
    if (!regio) return false;
    // Must have either a file OR enough typed text (or both)
    return files.length > 0 || text.trim().length >= 30;
  }, [regio, files, text]);
  // step 3 (pakket) is always valid — single or bundle is preselected
  const canStep4 = true;
  const canStep5 = first && last && email && gdpr && withdrawalWaiver;

  const validateDiscount = async () => {
    if (!discountCode.trim() || !email.trim()) {
      toast.error("Vul eerst je e-mail in om de code te valideren.");
      return;
    }
    setValidatingCode(true);
    try {
      const { data } = await api.post("/discount-codes/validate", {
        code: discountCode.trim().toUpperCase(),
        email: email.trim(),
      });
      setDiscountInfo(data);
      if (data.valid) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon code niet valideren.");
      setDiscountInfo(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handlePay = async () => {
    setSubmitting(true);
    try {
      const uploads = [];
      for (const f of files) {
        uploads.push({
          filename: f.name,
          mime: f.type,
          b64: await fileToB64(f),
        });
      }
      const payload = {
        offerte_type: type,
        regio,
        text_content: text.trim() ? text.trim() : undefined,
        uploads: uploads.length ? uploads : undefined,
        bundle,
        discount_code: discountInfo?.valid ? discountCode.trim().toUpperCase() : undefined,
        customer: {
          first_name: first,
          last_name: last,
          email,
          phone: phone || undefined,
          gdpr_accepted: gdpr,
          wants_business_recommendations: wantBizRecs,
        },
      };
      const { data } = await api.post("/analyses", payload);
      window.location.href = data.payment_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-oc-bg">
      <header className="bg-white border-b border-oc-border">
        <div className="oc-container py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-oc-primary">
            <ShieldIcon className="w-6 h-6 text-oc-secondary" />
            OfferteExpert
          </Link>
          <div className="flex items-center gap-2 text-[13px] text-oc-textSecondary">
            <Lock className="w-3.5 h-3.5" /> Beveiligd
          </div>
        </div>
      </header>

      <div className="oc-container py-12">
        {/* BE & NL availability banner — establishes trust + scope */}
        <div
          data-testid="benl-banner"
          className="max-w-2xl mx-auto mb-10 flex items-center justify-center gap-3 px-5 py-3 rounded-full bg-gradient-to-r from-oc-fair/10 via-white to-oc-fair/10 border border-oc-fair/25 shadow-[0_4px_18px_rgba(52,199,89,0.08)]"
        >
          <span className="flex h-2 w-2 rounded-full bg-oc-fair animate-pulse shrink-0" />
          <FlagsBENL size="sm" />
          <span className="text-[13px] sm:text-[14px] text-oc-textPrimary font-medium leading-none">
            Offerte-check beschikbaar voor <strong className="text-oc-primary">België</strong> &amp;{" "}
            <strong className="text-oc-primary">Nederland</strong>
          </span>
        </div>

        <ProgressBar step={step} />

        <div className="mt-14">
          {step === 1 && <StepType type={type} setType={setType} />}

          {step === 2 && (
            <StepUpload
              files={files}
              setFiles={setFiles}
              text={text}
              setText={setText}
              regio={regio}
              setRegio={setRegio}
            />
          )}

          {step === 3 && (
            <StepPakket
              bundle={bundle}
              setBundle={setBundle}
              singlePriceCents={singlePriceCents}
              bundlePriceCents={bundlePriceCents}
              baseAmountCents={baseAmountCents}
            />
          )}

          {step === 4 && (
            <StepGegevens
              first={first}
              setFirst={setFirst}
              last={last}
              setLast={setLast}
              email={email}
              setEmail={setEmail}
              phone={phone}
              setPhone={setPhone}
              gdpr={gdpr}
              setGdpr={setGdpr}
              withdrawalWaiver={withdrawalWaiver}
              setWithdrawalWaiver={setWithdrawalWaiver}
              wantBizRecs={wantBizRecs}
              setWantBizRecs={setWantBizRecs}
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              discountInfo={discountInfo}
              setDiscountInfo={setDiscountInfo}
              validatingCode={validatingCode}
              validateDiscount={validateDiscount}
              finalAmountCents={finalAmountCents}
              baseAmountCents={baseAmountCents}
            />
          )}

          {step === 5 && (
            <StepBetaling
              type={type}
              regio={regio}
              email={email}
              bundle={bundle}
              discountInfo={discountInfo}
              baseAmountCents={baseAmountCents}
              appliedDiscount={appliedDiscount}
              finalAmountCents={finalAmountCents}
              submitting={submitting}
              handlePay={handlePay}
            />
          )}
        </div>

        {step < 5 && (
          <div className="max-w-xl mx-auto mt-12 mb-20 sm:mb-28 flex items-center justify-between">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                data-testid="step-back"
                className="rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Terug
              </Button>
            ) : (
              <span />
            )}
            <Button
              data-testid="step-next"
              disabled={
                (step === 1 && !canStep2) ||
                (step === 2 && !canStep3) ||
                (step === 3 && !canStep4) ||
                (step === 4 && !canStep5)
              }
              onClick={() => setStep(step + 1)}
              className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-12 px-7 font-semibold transition-all hover:scale-[1.01]"
            >
              Volgende stap
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        {step === 5 && <div className="mb-20 sm:mb-28" />}
      </div>
    </div>
  );
}
